import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Mic, MicOff, Pin, Trash2, Volume2, VolumeX, X } from "lucide-react";

const initials = (name) => String(name || "Viewer").trim().slice(0, 1).toUpperCase() || "V";
export const EDUCATION_MESSAGE_IDLE_MS = 10_000;

export const CreatorAudienceSpeakerControls = ({
  speakerRequests = [],
  onInviteSpeaker,
  onMuteSpeaker,
  onEndSpeaker,
  speakerBusyId = null,
  speakerMode = "education",
  className = "",
}) => {
  const visibleSpeakers = speakerRequests.filter((speaker) => (
    ["waiting", "invited", "speaking", "muted"].includes(speaker.status)
  ));
  if (!visibleSpeakers.length) return null;

  const waitingSpeakers = visibleSpeakers.filter((speaker) => speaker.status === "waiting");
  const activeSpeakers = visibleSpeakers.filter((speaker) => speaker.status !== "waiting");
  const isPodcastCallIn = speakerMode === "podcast";
  const isReligionMic = speakerMode === "religion";
  const speakerIcon = isPodcastCallIn ? "☎️" : isReligionMic ? "🎙️" : "🖐️";
  const speakerRequestName = isPodcastCallIn ? "Call-In" : isReligionMic ? "Mic request" : "raised hand";

  return (
    <div className={`pointer-events-auto relative z-10 flex shrink-0 flex-col gap-1.5 ${className}`} data-testid="audience-speaker-controls-above-chat">
      {waitingSpeakers.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="waiting-speaker-controls">
          {waitingSpeakers.map((speaker) => (
            <button key={`speaker-${speaker.id}`} type="button" onClick={() => onInviteSpeaker?.(speaker)} disabled={speakerBusyId === speaker.id} className="pointer-events-auto flex w-fit shrink-0 touch-manipulation items-center gap-1 rounded-full border border-amber-300/25 bg-black/55 px-2 py-1 text-2xl drop-shadow-[0_2px_5px_rgba(0,0,0,.9)] transition hover:scale-105 active:scale-95 disabled:opacity-50" aria-label={`Accept ${speaker.user_name || "viewer"}'s ${speakerRequestName}`} title={`Accept ${speaker.user_name || "viewer"}'s ${speakerRequestName}`} data-testid={`invite-speaker-${speaker.id}`}>
              <span aria-hidden="true">{speakerIcon}</span><Mic className="h-3.5 w-3.5 text-amber-200" aria-hidden="true" />
            </button>
          ))}
        </div>
      )}
      {activeSpeakers.map((speaker) => {
        const isSpeaking = speaker.status === "speaking" && speaker.mic_enabled;
        const isMuted = speaker.status === "muted";
        return (
          <article key={`speaker-${speaker.id}`} className="pointer-events-auto flex shrink-0 items-center gap-2 rounded-xl border border-amber-300/20 bg-black/70 px-2 py-1.5 backdrop-blur-md" data-testid={`active-speaker-${speaker.id}`}>
            <div className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-amber-500/30 text-[11px] font-black text-white">{speaker.user_picture ? <img src={speaker.user_picture} alt="" className="h-full w-full object-cover" /> : initials(speaker.user_name)}</div>
            <div className="min-w-0 flex-1"><p className="truncate text-[11px] font-bold text-white">{speaker.user_name || "Viewer"}</p><p className={`text-[10px] font-semibold ${isSpeaking ? "text-emerald-300" : isMuted ? "text-red-300" : "text-amber-200"}`}>{isSpeaking ? "Speaking" : isMuted ? "Mic muted" : "Invited · mic off"}</p></div>
            {isMuted ? (
              <button type="button" onClick={() => onInviteSpeaker?.(speaker)} disabled={speakerBusyId === speaker.id} className="flex h-8 items-center gap-1 rounded-full bg-emerald-500/20 px-2 text-[10px] font-black text-emerald-100 disabled:opacity-50" aria-label={`Allow ${speaker.user_name || "viewer"} microphone`} data-testid={`unmute-speaker-${speaker.id}`}><Mic className="h-3.5 w-3.5" /> Allow mic</button>
            ) : (
              <button type="button" onClick={() => onMuteSpeaker?.(speaker)} disabled={speakerBusyId === speaker.id} className="flex h-8 items-center gap-1 rounded-full bg-red-500/20 px-2 text-[10px] font-black text-red-100 disabled:opacity-50" aria-label={`Mute ${speaker.user_name || "viewer"} microphone`} data-testid={`mute-speaker-${speaker.id}`}><MicOff className="h-3.5 w-3.5" /> Mic off</button>
            )}
            <button type="button" onClick={() => onEndSpeaker?.(speaker)} disabled={speakerBusyId === speaker.id} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-gray-200 disabled:opacity-50" aria-label={`End ${speaker.user_name || "viewer"} speaker access`} data-testid={`end-speaker-${speaker.id}`}><X className="h-3.5 w-3.5" /></button>
          </article>
        );
      })}
    </div>
  );
};

const EducationQuestionQueue = ({
  questions = [],
  onDelete,
  onPinQuestion,
  onMuteUser,
  onUnmuteUser,
  speakerRequests = [],
  onInviteSpeaker,
  onMuteSpeaker,
  onEndSpeaker,
  speakerBusyId = null,
  speakerMode = "education",
  className = "",
}) => {
  const touchStartRef = useRef(null);
  const historyRef = useRef(null);
  const stickToLatestRef = useRef(true);
  const idleTimerRef = useRef(null);
  const [deleteOpenId, setDeleteOpenId] = useState(null);
  const [messagesIdle, setMessagesIdle] = useState(false);
  const visibleQuestions = useMemo(
    () => questions.filter((question) => question.question_state !== "deleted"),
    [questions],
  );
  const pinnedQuestion = useMemo(
    () => [...visibleQuestions].reverse().find((question) => question.is_pinned) || null,
    [visibleQuestions],
  );
  const historyQuestions = useMemo(
    () => visibleQuestions.filter(
      (question) => !pinnedQuestion || String(question.id) !== String(pinnedQuestion.id),
    ),
    [pinnedQuestion, visibleQuestions],
  );
  const visibleSpeakers = speakerRequests.filter((speaker) => (
    ["waiting", "invited", "speaking", "muted"].includes(speaker.status)
  ));
  const isPodcastCallIn = speakerMode === "podcast";
  const isReligionMic = speakerMode === "religion";
  const queueLabel = isPodcastCallIn
    ? "Podcast Call-In queue"
    : isReligionMic ? "Religion Mic queue" : "Private Education messages";
  const latestQuestionId = visibleQuestions[visibleQuestions.length - 1]?.id;

  const keepMessagesVisible = useCallback(() => {
    setMessagesIdle(false);
    if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
    idleTimerRef.current = historyQuestions.length
      ? window.setTimeout(() => setMessagesIdle(true), EDUCATION_MESSAGE_IDLE_MS)
      : null;
  }, [historyQuestions.length]);

  useEffect(() => {
    keepMessagesVisible();
  }, [keepMessagesVisible, latestQuestionId]);

  useEffect(() => () => {
    if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
  }, []);

  useEffect(() => {
    if (!messagesIdle || !historyQuestions.length) return undefined;
    const revealOnLocalTap = () => keepMessagesVisible();
    document.addEventListener("pointerdown", revealOnLocalTap, { passive: true });
    return () => document.removeEventListener("pointerdown", revealOnLocalTap);
  }, [historyQuestions.length, keepMessagesVisible, messagesIdle]);

  useEffect(() => {
    const history = historyRef.current;
    if (!history || !stickToLatestRef.current) return;
    if (typeof history.scrollTo === "function") {
      history.scrollTo({ top: history.scrollHeight, behavior: "smooth" });
    } else {
      history.scrollTop = history.scrollHeight;
    }
  }, [latestQuestionId]);

  const handleHistoryScroll = useCallback(() => {
    keepMessagesVisible();
    const history = historyRef.current;
    if (!history) return;
    const distanceFromLatest = history.scrollHeight - history.scrollTop - history.clientHeight;
    stickToLatestRef.current = distanceFromLatest <= 40;
  }, [keepMessagesVisible]);

  if (!visibleQuestions.length && !visibleSpeakers.length) return null;

  const renderQuestion = (question, pinned = false) => {
    const deleteOpen = deleteOpenId === question.id;
    const username = question.user_name || question.username || "Viewer";
    const isChatMuted = Boolean(question.is_chat_muted);
    const canMute = Boolean(onMuteUser && question.user_id && !isChatMuted);
    const canUnmute = Boolean(onUnmuteUser && question.user_id && isChatMuted);
    return (
      <article
        key={question.id}
        className={`pointer-events-auto relative shrink-0 overflow-hidden rounded-lg border shadow-lg backdrop-blur-md ${
          pinned ? "border-blue-300/45 bg-[#071226]/90" : "border-white/15 bg-black/72"
        }`}
        data-testid={`private-education-message-${question.id}`}
        onTouchStart={(event) => {
          touchStartRef.current = event.touches[0]?.clientX ?? null;
        }}
        onTouchEnd={(event) => {
          const start = touchStartRef.current;
          const end = event.changedTouches[0]?.clientX;
          touchStartRef.current = null;
          if (start != null && end != null && start - end > 55) setDeleteOpenId(question.id);
          if (start != null && end != null && end - start > 45) setDeleteOpenId(null);
        }}
      >
        {deleteOpen && onDelete && (
          <button type="button" onClick={() => onDelete(question)} className="absolute inset-y-0 right-0 flex w-20 items-center justify-center gap-1 rounded-lg bg-red-600 text-xs font-black text-white" aria-label={`Delete question from ${username}`}>
            <Trash2 className="h-4 w-4" /> Delete
          </button>
        )}
        <div className="relative px-1.5 py-1 transition-transform duration-200" style={{ transform: deleteOpen ? "translateX(-5rem)" : "translateX(0)" }} onClick={() => deleteOpen && setDeleteOpenId(null)}>
          <div className="flex items-start gap-1.5">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/25 text-[10px] font-black text-white shadow-md" style={{ background: "linear-gradient(135deg,#3b82f6,#312e81)" }}>
              {question.user_picture ? <img src={question.user_picture} alt="" className="h-full w-full object-cover" /> : initials(username)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-1">
                <p className="min-w-0 flex-1 truncate text-[11px] font-extrabold text-gray-100">{username} · Private</p>
                {pinned && <Pin className="h-3 w-3 shrink-0 fill-blue-300 text-blue-300" aria-label="Pinned private message" />}
              </div>
              <p className="whitespace-pre-wrap break-words text-[13px] font-bold leading-[1.2] text-white [text-shadow:0_1px_4px_rgba(0,0,0,.95)]">{question.message}</p>
              {(onPinQuestion || canMute || canUnmute) && (
                <div className="mt-1 flex items-center justify-between gap-3 border-t border-white/10 pt-1">
                  {onPinQuestion ? (
                    <button type="button" onClick={(event) => { event.stopPropagation(); onPinQuestion(question); }} className={`inline-flex h-6 shrink-0 touch-manipulation items-center gap-1 rounded-full border px-2 text-[9px] font-black ${pinned ? "border-blue-300/40 bg-blue-500/25 text-blue-100" : "border-white/20 bg-black/50 text-gray-100"}`} aria-label={`${pinned ? "Unpin" : "Pin"} private message from ${username}`} data-testid={`pin-question-${question.id}`}>
                      <Pin className={`h-3 w-3 ${pinned ? "fill-blue-200" : ""}`} /><span>{pinned ? "Unpin" : "Pin"}</span>
                    </button>
                  ) : <span />}
                  {(canMute || canUnmute) && (
                    <button type="button" onClick={(event) => { event.stopPropagation(); if (isChatMuted) onUnmuteUser(question); else onMuteUser(question); }} className={`inline-flex h-6 shrink-0 touch-manipulation items-center gap-1 rounded-full border px-2 text-[9px] font-black ${isChatMuted ? "border-emerald-300/35 bg-emerald-500/20 text-emerald-100" : "border-red-300/35 bg-red-500/20 text-red-100"}`} aria-label={`${isChatMuted ? "Unmute" : "Mute"} ${username} for this event`} data-testid={`${isChatMuted ? "unmute" : "mute"}-question-user-${question.id}`}>
                      {isChatMuted ? <Volume2 className="h-3 w-3" /> : <VolumeX className="h-3 w-3" />}<span>{isChatMuted ? "Unmute" : "Mute"}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </article>
    );
  };

  return (
    <section className={`absolute bottom-[5.25rem] left-3 z-50 flex max-h-[min(30dvh,15rem)] w-[min(72vw,20rem)] flex-col overflow-hidden sm:left-4 sm:w-[min(58vw,22rem)] ${className}`} aria-label={queueLabel} data-testid="education-question-queue" data-speaker-mode={speakerMode} data-messages-idle={messagesIdle ? "true" : "false"}>
      {visibleSpeakers.length > 0 && (
        <CreatorAudienceSpeakerControls
          speakerRequests={visibleSpeakers}
          onInviteSpeaker={onInviteSpeaker}
          onMuteSpeaker={onMuteSpeaker}
          onEndSpeaker={onEndSpeaker}
          speakerBusyId={speakerBusyId}
          speakerMode={speakerMode}
          className="pb-1.5"
        />
      )}
      {pinnedQuestion && <div className="shrink-0 pb-1.5">{renderQuestion(pinnedQuestion, true)}</div>}
      <div ref={historyRef} onScroll={handleHistoryScroll} className="pointer-events-auto flex min-h-0 flex-1 touch-pan-y flex-col gap-1.5 overflow-x-hidden overflow-y-auto overscroll-y-contain pb-1 pr-1 [scrollbar-color:rgba(255,255,255,.35)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/30 [&::-webkit-scrollbar]:w-1.5" style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }} tabIndex={0} role="log" aria-live="polite" aria-label="Scrollable private Education messages" data-testid="education-private-message-history">
        {!messagesIdle ? historyQuestions.map((question) => renderQuestion(question)) : null}
      </div>
    </section>
  );
};

export default EducationQuestionQueue;
